import PromisedTimeout from 'helpers/promised-timeout';
import { getErrorMessage } from 'helpers/sentry';

import { TxFailed } from 'types/stellar';

// Backoff between resubmission attempts (ms). Length defines the max retries.
const TRANSACTION_SUBMIT_RETRY_DELAYS = [1500, 3000, 5000, 8000];

// Congestion-related failures that are safe to auto-retry by resubmitting the
// SAME signed transaction. Both Horizon and the Soroban RPC dedupe by tx hash,
// so a resubmit either returns the already-applied result or gets a fresh shot
// once the surge eases. Codes that bake state into the envelope (tx_bad_seq,
// tx_too_late) are intentionally excluded — they can't be fixed by a plain
// resubmit and require rebuilding + re-signing.
const RETRYABLE_TRANSACTION_SUBMIT_CODES = new Set(['timeout', 'tx_insufficient_fee']);

// Soroban RPC congestion/timeout signals surfaced as Error messages by
// connection.ts (TRY_AGAIN_LATER and the pollTransaction timeout).
const RETRYABLE_TRANSACTION_SUBMIT_MESSAGES = ['try again later', 'timeout', 'timed out'];

type ErrorWithResponse = {
    response?: {
        data?: Partial<TxFailed>;
        status?: number;
    };
};

export function normalizeTransactionResultCode(code: string): string {
    return code
        .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
        .replace(/^_/, '')
        .toLowerCase();
}

export function isRetryableTransactionSubmitError(error: unknown): boolean {
    const errorWithResponse = error as ErrorWithResponse;

    // Classic Horizon: transaction-level result code from the REST error body.
    const resultCode = errorWithResponse.response?.data?.extras?.result_codes?.transaction;

    if (
        resultCode &&
        RETRYABLE_TRANSACTION_SUBMIT_CODES.has(normalizeTransactionResultCode(resultCode))
    ) {
        return true;
    }

    // Network-level timeout from Horizon REST.
    if (errorWithResponse.response?.status === 408 || errorWithResponse.response?.status === 504) {
        return true;
    }

    // Soroban RPC + transport-level signals are only available as a message.
    const message = getErrorMessage(error)?.toLowerCase() ?? '';

    return RETRYABLE_TRANSACTION_SUBMIT_MESSAGES.some(signal => message.includes(signal));
}

export async function retryTransactionSubmit<T>(submit: () => Promise<T>): Promise<T> {
    for (let attemptIndex = 0; ; attemptIndex++) {
        try {
            return await submit();
        } catch (error) {
            const retryDelay = TRANSACTION_SUBMIT_RETRY_DELAYS[attemptIndex];

            if (!retryDelay || !isRetryableTransactionSubmitError(error)) {
                throw error;
            }

            await PromisedTimeout(retryDelay);
        }
    }
}
